import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, Toggle, deletedRefProps, normalizeOptions } from './fields';
import DateField from './DateField';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { EQUIPMENT_SIZES, EQUIPMENT_TYPES } from '../../constants/orderFieldOptions';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { ScacOption, getScacs } from '../../services/scacs.service';
import { ScacPerDiemInfo, getScacPerDiemInfo } from '../../services/scacprofiles.service';

/**
 * Bespoke edit/create drawer for Steamship Line Per Diem (cms.sslperdiem) — an effective-
 * dated per-diem rate schedule per (SCAC, size, equiptype). SCAC/Size/Type selects, an
 * effective date, import/export free-days + Business/Calendar day-types, a Tier 1/2/3
 * import+export amount grid, and reuse rules. A readonly "Per Diem Information" block
 * (contact/phone/fax/email/comments from the active scacprofiles row) shows at the foot,
 * refreshing with the SCAC. Only SCAC is required; when both Tier 2 + Tier 3 Start are set,
 * Tier 3 Start must exceed Tier 2 Start. (scac,size,equiptype,effectivedate) is UNIQUE.
 */
const TABLE = 'sslperdiem';

const s = (v: any): string => (v == null ? '' : String(v));
const mdyToIso = (v: any): string => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s(v));
  return m ? `${m[3]}-${m[1]}-${m[2]}` : '';
};

const DAYTYPE_OPTS = [{ value: 'BUSINESS', label: 'Business' }, { value: 'CALENDAR', label: 'Calendar' }];
const REUSETYPE_OPTS = [{ value: 'stoprestart', label: 'STOP AND RESTART' }, { value: 'additionaldays', label: 'ADDITIONAL DAYS' }];

// Editable numeric/select fields we diff on save (effectivedate handled separately).
const NUM_KEYS = [
  'importdays', 'importamount', 'importtier2days', 'importtier2amount', 'importtier3days', 'importtier3amount',
  'exportdays', 'exportamount', 'exporttier2days', 'exporttier2amount', 'exporttier3days', 'exporttier3amount',
  'reusedays',
];
const SELECT_KEYS = ['scac', 'size', 'equiptype', 'importdaystype', 'exportdaystype', 'reusetype', 'reusedaystype'];

// The day-type selects deliberately carry NO `|| 'BUSINESS'` display fallback.
// These columns default to '' in the schema (cms.sslperdiem), and the save below
// patches diff-only (`if (s(form[k]) !== s(record[k]))`) — so a blank shown as
// BUSINESS would never be staged, never saved, and would read as correct forever.
// 195 of 3,967 live rows have a blank `reusedaystype` today. SelectField renders a
// blank as its "Select…" placeholder, which is the honest display and one pick
// away from fixed. See utils/forcedSelectOption for the rule.
const ScacPerDiemEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [scacOpts, setScacOpts] = useState<ScacOption[]>([]);
  const [form, setForm] = useState<TablemaintRow>(() => {
    const f: TablemaintRow = { scac: '', size: '', equiptype: '', effectivedate: '',
      importdaystype: 'BUSINESS', exportdaystype: 'BUSINESS', reusedaystype: 'BUSINESS', reusetype: 'stoprestart' };
    NUM_KEYS.forEach((k) => { f[k] = ''; });
    return f;
  });
  const [formDeleted, setFormDeleted] = useState(false);
  const [perDiem, setPerDiem] = useState<ScacPerDiemInfo | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getScacs().then(setScacOpts).catch(() => {});
    if (!isNew) tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
  }, [isNew, recno]);

  useEffect(() => {
    if (isNew) return;
    const f: TablemaintRow = {
      scac: s(record.scac), size: s(record.size), equiptype: s(record.equiptype),
      effectivedate: mdyToIso(record.effectivedate),
      importdaystype: s(record.importdaystype) || 'BUSINESS',
      exportdaystype: s(record.exportdaystype) || 'BUSINESS',
      reusedaystype: s(record.reusedaystype) || 'BUSINESS',
      reusetype: s(record.reusetype),
    };
    NUM_KEYS.forEach((k) => { f[k] = s(record[k]); });
    setForm(f);
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record, isNew]);

  // Load the readonly Per Diem Information block for the current SCAC (refreshes on change).
  useEffect(() => {
    const scac = s(form.scac);
    if (!scac) { setPerDiem(null); return; }
    let alive = true;
    getScacPerDiemInfo(scac).then((p) => { if (alive) setPerDiem(p); }).catch(() => { if (alive) setPerDiem(null); });
    return () => { alive = false; };
  }, [form.scac]);

  useEffect(() => { if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const sizeOpts = useMemo(() => normalizeOptions(EQUIPMENT_SIZES as unknown as string[]), []);
  const typeOpts = useMemo(() => normalizeOptions(EQUIPMENT_TYPES as unknown as string[]), []);

  const numVal = (k: string) => parseFloat(s(form[k]).trim());
  const tierBad = (side: 'import' | 'export') => {
    const t2 = numVal(`${side}tier2days`); const t3 = numVal(`${side}tier3days`);
    return t2 > 0 && t3 > 0 && t3 <= t2;
  };

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!s(form.scac).trim()) e.push('SCAC is required.');
    if (tierBad('import')) e.push('Import Tier 3 Start must be greater than Tier 2 Start.');
    if (tierBad('export')) e.push('Export Tier 3 Start must be greater than Tier 2 Start.');
    return e;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    // Full field payload (numbers: blank → '0'; effectivedate: ISO or blank).
    const full: TablemaintRow = { effectivedate: s(form.effectivedate).trim() };
    SELECT_KEYS.forEach((k) => { full[k] = s(form[k]); });
    NUM_KEYS.forEach((k) => { full[k] = s(form[k]).trim() || '0'; });
    try {
      if (isNew) {
        await tablemaintService.create(TABLE, full);
      } else {
        // Diff against the seeded record (effectivedate compared in ISO; numbers as strings).
        const patch: TablemaintRow = {};
        SELECT_KEYS.forEach((k) => { if (s(form[k]) !== s(record[k])) patch[k] = full[k]; });
        NUM_KEYS.forEach((k) => { if (s(form[k]).trim() !== s(record[k]).trim()) patch[k] = full[k]; });
        if (s(form.effectivedate).trim() !== mdyToIso(record.effectivedate)) patch.effectivedate = full.effectivedate;
        if (formDeleted !== (s(record.deleted) === 'Y')) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length === 0) { onClose(); return; }
        await tablemaintService.update(TABLE, recno, patch);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save per diem schedule.');
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, isNew, form, record, formDeleted, recno, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Per Diem' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add Per Diem Schedule' : `Edit Per Diem Schedule (#${recno})`;
  const numInput = 'w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm';
  const NumCell = (k: string, opts?: { invalid?: boolean; step?: string }) => (
    <input type="number" step={opts?.step ?? 'any'} min="0" value={s(form[k])} disabled={isSaving}
      onChange={(e) => setField(k, e.target.value)}
      className={`${numInput} ${opts?.invalid ? 'ring-2 ring-red-400 border-red-400' : ''}`} />
  );

  const pdRow = (label: string, value: string) => (
    <div className="flex gap-2">
      <span className="w-32 shrink-0 text-right text-xs font-medium text-muted">{label}</span>
      <span className="text-sm text-gray-800 dark:text-gray-200 break-words whitespace-pre-wrap">{value || '—'}</span>
    </div>
  );

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="56rem" footer={footer}>
      <div className="space-y-5">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Identity */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Schedule</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="SCAC" required value={s(form.scac)} onChange={(v) => setField('scac', v)} options={scacOpts} disabled={isSaving}
              {...deletedRefProps('SCAC', form.scac, form.scac, record.scacdeleted)} />
            <DateField label="Effective Date" value={s(form.effectivedate)} onChange={(v) => setField('effectivedate', v)} disabled={isSaving} />
            <SelectField label="Size" value={s(form.size)} onChange={(v) => setField('size', v)} options={sizeOpts} disabled={isSaving} />
            <SelectField label="Equip Type" value={s(form.equiptype)} onChange={(v) => setField('equiptype', v)} options={typeOpts} disabled={isSaving} />
          </div>
        </fieldset>

        {/* Free days */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Free Days</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-label mb-1">Import Days Free</label>
              {NumCell('importdays', { step: '1' })}
            </div>
            <SelectField label="Import Days Type" clearable={false} value={s(form.importdaystype)} onChange={(v) => setField('importdaystype', v)} options={DAYTYPE_OPTS} disabled={isSaving} />
            <div>
              <label className="block text-sm font-medium text-label mb-1">Export Days Free</label>
              {NumCell('exportdays', { step: '1' })}
            </div>
            <SelectField label="Export Days Type" clearable={false} value={s(form.exportdaystype)} onChange={(v) => setField('exportdaystype', v)} options={DAYTYPE_OPTS} disabled={isSaving} />
          </div>
        </fieldset>

        {/* Tier amounts grid */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Per Diem Amounts</legend>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-semibold text-muted">
                  <th className="w-16" />
                  <th className="px-2 py-1 text-left font-semibold">Tier 1 Amount</th>
                  <th className="px-2 py-1 text-left font-semibold">Tier 2 Start</th>
                  <th className="px-2 py-1 text-left font-semibold">Tier 2 Amount</th>
                  <th className="px-2 py-1 text-left font-semibold">Tier 3 Start</th>
                  <th className="px-2 py-1 text-left font-semibold">Tier 3 Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="pr-2 text-right text-sm font-medium text-gray-600 dark:text-gray-300">Import</td>
                  <td className="px-1 py-1">{NumCell('importamount')}</td>
                  <td className="px-1 py-1">{NumCell('importtier2days', { step: '1' })}</td>
                  <td className="px-1 py-1">{NumCell('importtier2amount')}</td>
                  <td className="px-1 py-1">{NumCell('importtier3days', { step: '1', invalid: showErrors && tierBad('import') })}</td>
                  <td className="px-1 py-1">{NumCell('importtier3amount')}</td>
                </tr>
                <tr>
                  <td className="pr-2 text-right text-sm font-medium text-gray-600 dark:text-gray-300">Export</td>
                  <td className="px-1 py-1">{NumCell('exportamount')}</td>
                  <td className="px-1 py-1">{NumCell('exporttier2days', { step: '1' })}</td>
                  <td className="px-1 py-1">{NumCell('exporttier2amount')}</td>
                  <td className="px-1 py-1">{NumCell('exporttier3days', { step: '1', invalid: showErrors && tierBad('export') })}</td>
                  <td className="px-1 py-1">{NumCell('exporttier3amount')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </fieldset>

        {/* Reuse */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Reuse</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SelectField label="Reuse Type" clearable={false} value={s(form.reusetype)} onChange={(v) => setField('reusetype', v)} options={REUSETYPE_OPTS} disabled={isSaving} />
            <div>
              <label className="block text-sm font-medium text-label mb-1">Reuse Days</label>
              {NumCell('reusedays', { step: '1' })}
            </div>
            <SelectField label="Reuse Days Type" clearable={false} value={s(form.reusedaystype)} onChange={(v) => setField('reusedaystype', v)} options={DAYTYPE_OPTS} disabled={isSaving} />
          </div>
        </fieldset>

        {/* Per Diem Information (readonly, from the active scacprofiles row) */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/40 p-4">
          <legend className="px-2 text-sm font-semibold text-label">Per Diem Information</legend>
          {perDiem ? (
            <div className="space-y-1.5">
              {pdRow('Per Diem Contact', perDiem.perdiemcontact)}
              {pdRow('Per Diem Phone', perDiem.perdiemphone)}
              {pdRow('Per Diem Fax', perDiem.perdiemfax)}
              {pdRow('Per Diem Email', perDiem.perdiememail)}
              {pdRow('Comments', perDiem.comments)}
            </div>
          ) : (
            <p className="text-sm text-muted">{s(form.scac) ? 'No per diem information on file for this SCAC.' : 'Select a SCAC to see its per diem contact information.'}</p>
          )}
        </fieldset>

        {!isNew && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <Toggle label="Deleted" checked={formDeleted} disabled={isSaving} onChange={setFormDeleted}
              hint="Soft-deletes this per diem schedule — hidden from filters but preserved in history. Uncheck and save to restore." />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default ScacPerDiemEditDrawer;
